import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Film, Plus, Trash2, Loader2, X, Pencil, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const emptyForm = {
  title: "", media_type: "movie", year: "", director: "",
  stars: "", description: "", seasons: "", imdb_url: "", rt_score: "",
};

const AdminFilmography = () => {
  const [universes, setUniverses] = useState([]);
  const [filmography, setFilmography] = useState([]);
  const [selectedUniverse, setSelectedUniverse] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  useEffect(() => {
    axios.get(`${API}/universes`).then(r => setUniverses(r.data)).catch(() => {});
  }, []);

  const fetchFilmography = async () => {
    if (!selectedUniverse) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/filmography?universe_id=${selectedUniverse}`);
      setFilmography(r.data);
    } catch { toast.error("Failed to load filmography"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFilmography(); }, [selectedUniverse]);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  };

  const openEdit = (film) => {
    setEditingId(film.id);
    setForm({
      title: film.title || "",
      media_type: film.media_type || "movie",
      year: film.year || "",
      director: film.director || "",
      stars: film.stars?.join(", ") || "",
      description: film.description || "",
      seasons: film.seasons?.toString() || "",
      imdb_url: film.imdb_url || "",
      rt_score: film.rt_score?.toString() || "",
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("Title required");
    const payload = {
      title: form.title,
      universe_id: selectedUniverse,
      media_type: form.media_type,
      year: form.year || null,
      director: form.director || null,
      description: form.description || null,
      stars: form.stars ? form.stars.split(",").map(s => s.trim()).filter(Boolean) : [],
      seasons: form.seasons ? parseInt(form.seasons) : null,
      imdb_url: form.imdb_url || null,
      rt_score: form.rt_score ? parseInt(form.rt_score) : null,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/filmography/${editingId}`, payload);
        toast.success("Entry updated");
      } else {
        await axios.post(`${API}/filmography`, payload);
        toast.success("Entry added");
      }
      setForm({ ...emptyForm });
      setShowForm(false);
      setEditingId(null);
      fetchFilmography();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await axios.delete(`${API}/filmography/${id}`);
      setFilmography(prev => prev.filter(f => f.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const movies = filmography.filter(f => f.media_type === "movie");
  const shows = filmography.filter(f => f.media_type === "tv_show");

  const FilmRow = ({ film }) => (
    <div className="card-surface p-4 flex items-center justify-between gap-4" data-testid={`admin-film-${film.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white font-semibold">{film.title}</span>
          {film.year && <span className="text-xs font-mono text-amber-500/70">{film.year}</span>}
          {film.seasons && <span className="text-xs text-slate-500">{film.seasons}s</span>}
          {film.rt_score != null && <span className="text-xs font-bold text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">{film.rt_score}%</span>}
          {film.imdb_url && <span className="text-[10px] font-bold text-yellow-400 bg-yellow-400/10 px-1.5 py-0.5 rounded">IMDb</span>}
        </div>
        {film.director && <p className="text-xs text-slate-500">{film.director}</p>}
        {film.stars?.length > 0 && <p className="text-xs text-slate-400 truncate">{film.stars.join(", ")}</p>}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button variant="ghost" size="sm" onClick={() => openEdit(film)} className="text-amber-400 hover:text-amber-300" data-testid={`edit-film-${film.id}`}>
          <Pencil className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => handleDelete(film.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-film-${film.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <AdminLayout title="Filmography Management">
      <div className="space-y-6" data-testid="admin-filmography">
        {/* Universe Selector */}
        <div className="flex items-center gap-4">
          <div className="w-72">
            <Select value={selectedUniverse} onValueChange={v => { setSelectedUniverse(v); setShowForm(false); setEditingId(null); }}>
              <SelectTrigger data-testid="universe-select">
                <SelectValue placeholder="Select Universe" />
              </SelectTrigger>
              <SelectContent>
                {universes.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedUniverse && (
            <Button onClick={showForm && !editingId ? () => setShowForm(false) : openAdd} data-testid="add-filmography-btn">
              {showForm && !editingId ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {showForm && !editingId ? "Cancel" : "Add Entry"}
            </Button>
          )}
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="card-surface p-6 space-y-4" data-testid="filmography-form">
            <h3 className="text-white font-bold font-['Outfit']">{editingId ? "Edit Entry" : "Add New Entry"}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Title *</Label>
                <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="Title" data-testid="film-title-input" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.media_type} onValueChange={v => setForm(p => ({...p, media_type: v}))}>
                  <SelectTrigger data-testid="film-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="movie">Movie</SelectItem>
                    <SelectItem value="tv_show">TV Show</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Year</Label>
                <Input value={form.year} onChange={e => setForm(p => ({...p, year: e.target.value}))} placeholder="2024 or 2019-present" data-testid="film-year-input" />
              </div>
              <div>
                <Label>{form.media_type === "movie" ? "Director" : "Creator"}</Label>
                <Input value={form.director} onChange={e => setForm(p => ({...p, director: e.target.value}))} placeholder="Name" data-testid="film-director-input" />
              </div>
              <div>
                <Label>Stars (comma separated)</Label>
                <Input value={form.stars} onChange={e => setForm(p => ({...p, stars: e.target.value}))} placeholder="Actor 1, Actor 2" data-testid="film-stars-input" />
              </div>
              {form.media_type === "tv_show" && (
                <div>
                  <Label>Seasons</Label>
                  <Input type="number" value={form.seasons} onChange={e => setForm(p => ({...p, seasons: e.target.value}))} placeholder="3" data-testid="film-seasons-input" />
                </div>
              )}
              <div>
                <Label>IMDB URL</Label>
                <Input value={form.imdb_url} onChange={e => setForm(p => ({...p, imdb_url: e.target.value}))} placeholder="https://www.imdb.com/title/..." data-testid="film-imdb-input" />
              </div>
              <div>
                <Label>Rotten Tomatoes Score (%)</Label>
                <Input type="number" min="0" max="100" value={form.rt_score} onChange={e => setForm(p => ({...p, rt_score: e.target.value}))} placeholder="85" data-testid="film-rt-input" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} placeholder="Brief synopsis" rows={2} data-testid="film-desc-input" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" data-testid="film-submit-btn">
                {editingId ? <><Save className="w-4 h-4 mr-2" /> Update</> : <><Plus className="w-4 h-4 mr-2" /> Add</>}
              </Button>
              {editingId && (
                <Button type="button" variant="ghost" onClick={() => { setShowForm(false); setEditingId(null); }} data-testid="film-cancel-edit">Cancel</Button>
              )}
            </div>
          </form>
        )}

        {/* Content */}
        {!selectedUniverse ? (
          <p className="text-slate-500 text-center py-12">Select a universe to manage its filmography</p>
        ) : loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
        ) : filmography.length === 0 ? (
          <p className="text-slate-500 text-center py-12">No filmography entries yet. Add one above.</p>
        ) : (
          <div className="space-y-6">
            {movies.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2"><Film className="w-5 h-5 text-amber-500" /> Movies ({movies.length})</h3>
                <div className="space-y-2">{movies.map(f => <FilmRow key={f.id} film={f} />)}</div>
              </div>
            )}
            {shows.length > 0 && (
              <div>
                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="2" y="7" width="20" height="15" rx="2" /><path d="M17 2l-5 5-5-5" /></svg>
                  TV Shows ({shows.length})
                </h3>
                <div className="space-y-2">{shows.map(f => <FilmRow key={f.id} film={f} />)}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminFilmography;
